<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 dark:bg-black/75 backdrop-blur-md animate-fade-in">
    <div class="followup-modal-root relative w-full max-w-3xl max-h-[92vh] flex flex-col bg-white/90 dark:bg-[#141518]/95 backdrop-blur-2xl border border-white/80 dark:border-white/10 rounded-2xl shadow-2xl overflow-hidden animate-zoom-in">
      
      <!-- HEADER DO MODAL (DESIGN GLASS) -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200/60 dark:border-white/10 bg-white/40 dark:bg-[#1a1b20]/40 backdrop-blur-md">
        <div class="flex items-center gap-3">
          <div class="size-10 rounded-xl bg-blue-50 dark:bg-[#2781F6]/15 border border-blue-200/70 dark:border-[#2781F6]/30 text-[#2781F6] flex items-center justify-center shadow-xs">
            <span class="i-lucide-bot size-5" />
          </div>
          <div>
            <h3 class="text-base font-black text-slate-900 dark:text-white tracking-tight">
              {{ isEditing ? 'Editar Régua de Follow-up' : 'Nova Régua de Follow-up Inteligente' }}
            </h3>
            <p class="text-xs text-slate-500 dark:text-slate-400">
              Automatize a recuperação de leads e o reengajamento com IA nativa
            </p>
          </div>
        </div>

        <button
          @click="$emit('close')"
          type="button"
          class="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-white/60 dark:hover:bg-white/10 transition-colors cursor-pointer"
        >
          <span class="i-lucide-x size-5" />
        </button>
      </div>

      <!-- TABS DE NAVEGAÇÃO INTERNA -->
      <div class="flex items-center gap-2 px-6 pt-3 border-b border-slate-200/60 dark:border-white/10 bg-white/30 dark:bg-[#141518]/30 backdrop-blur-md overflow-x-auto">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="activeTab = tab.id"
          type="button"
          :class="[
            activeTab === tab.id
              ? 'border-[#2781F6] text-[#2781F6] font-black'
              : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 font-bold'
          ]"
          class="flex items-center gap-2 pb-2.5 px-2 text-xs border-b-2 transition-colors whitespace-nowrap cursor-pointer"
        >
          <span :class="tab.icon" class="size-4" />
          <span>{{ tab.name }}</span>
        </button>
      </div>

      <!-- CORPO DO MODAL (FORMULÁRIO GLASS) -->
      <div class="flex-1 overflow-y-auto p-6 space-y-5 bg-transparent">
        
        <!-- ================= ABA 1: FUNIL & GATILHOS ================= -->
        <div v-show="activeTab === 'general'" class="space-y-4">
          <!-- Nome da Régua -->
          <div class="flex flex-col gap-1.5">
            <label class="text-xs font-bold text-slate-800 dark:text-slate-200">
              Nome da Régua de Follow-up *
            </label>
            <input
              v-model="form.name"
              type="text"
              placeholder="Ex: 01 RECEPÇÃO QUALIFICAÇÃO"
              class="w-full h-10 px-3.5 text-xs font-medium bg-white/70 dark:bg-[#1a1b20]/70 border border-slate-200/80 dark:border-[#2c2d33] rounded-xl text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:border-[#2781F6] focus:bg-white dark:focus:bg-[#202128] transition-all"
            />
          </div>

          <!-- Seleção de Funil e Etapa do Kanban -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <div class="flex flex-col gap-1.5">
              <label class="text-xs font-bold text-slate-800 dark:text-slate-200">
                Funil do Kanban *
              </label>
              <select
                v-model="form.funnel_attribute_key"
                @change="onFunnelChange"
                class="w-full h-10 px-3.5 text-xs font-medium bg-white/70 dark:bg-[#1a1b20]/70 border border-slate-200/80 dark:border-[#2c2d33] rounded-xl text-slate-900 dark:text-slate-100 focus:outline-none focus:border-[#2781F6] cursor-pointer"
              >
                <option value="" disabled>Selecione o Funil</option>
                <option v-for="funnel in funnelsList" :key="funnel.id" :value="funnel.attribute_key">
                  {{ funnel.attribute_display_name }}
                </option>
              </select>
            </div>

            <div class="flex flex-col gap-1.5">
              <label class="text-xs font-bold text-slate-800 dark:text-slate-200">
                Etapa Gatilho (Trigger Stage) *
              </label>
              <select
                v-model="form.stage_name"
                :disabled="!form.funnel_attribute_key"
                class="w-full h-10 px-3.5 text-xs font-medium bg-white/70 dark:bg-[#1a1b20]/70 border border-slate-200/80 dark:border-[#2c2d33] rounded-xl text-slate-900 dark:text-slate-100 focus:outline-none focus:border-[#2781F6] cursor-pointer disabled:opacity-50"
              >
                <option value="" disabled>Selecione a Etapa</option>
                <option v-for="stg in currentFunnelStages" :key="stg" :value="stg">
                  {{ stg }}
                </option>
              </select>
            </div>
          </div>

          <!-- Caixas de Entrada Permitidas (WhatsApp / Canais) -->
          <div class="flex flex-col gap-2 p-4 rounded-2xl border border-slate-200/70 dark:border-white/10 bg-white/50 dark:bg-[#181920]/60 backdrop-blur-md">
            <div class="flex items-center justify-between">
              <label class="text-xs font-bold text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                <span>📱 Caixas de Entrada (Canais de Atendimento)</span>
                <span v-if="form.inbox_ids.length" class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#2781F6]/15 text-[#2781F6] border border-[#2781F6]/30">
                  {{ form.inbox_ids.length }} de {{ inboxesList.length }} selecionada(s)
                </span>
                <span v-else class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30">
                  Válido para Todas as Caixas (Padrão)
                </span>
              </label>
              
              <div class="flex items-center gap-2 text-xs">
                <button
                  type="button"
                  @click="selectAllInboxes"
                  class="text-[#2781F6] hover:underline cursor-pointer font-bold"
                >
                  Marcar Todas
                </button>
                <span class="text-slate-300 dark:text-slate-700">•</span>
                <button
                  type="button"
                  @click="clearAllInboxes"
                  class="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 cursor-pointer font-medium"
                >
                  Limpar
                </button>
              </div>
            </div>

            <!-- Lista de Caixas de Entrada Selecionáveis -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-36 overflow-y-auto p-1">
              <div
                v-for="inbox in inboxesList"
                :key="inbox.id"
                @click="toggleInbox(inbox.id)"
                :class="[
                  form.inbox_ids.includes(inbox.id)
                    ? 'border-[#2781F6] bg-[#2781F6]/10 text-slate-900 dark:text-white shadow-2xs'
                    : 'border-slate-200/80 dark:border-slate-800 bg-white/70 dark:bg-[#1a1b20]/70 text-slate-700 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-700'
                ]"
                class="flex items-center gap-2.5 p-2 rounded-xl border cursor-pointer select-none transition-all"
              >
                <input
                  type="checkbox"
                  :checked="form.inbox_ids.includes(inbox.id)"
                  class="size-4 rounded border-slate-300 dark:border-slate-600 text-[#2781F6] focus:ring-0 cursor-pointer pointer-events-none"
                />
                <div class="flex-1 min-w-0 flex items-center justify-between gap-1.5">
                  <span class="text-xs font-semibold truncate">{{ inbox.name }}</span>
                  <span
                    class="text-[10px] px-1.5 py-0.2 rounded font-bold shrink-0"
                    :class="getChannelBadgeClass(inbox.channel_type)"
                  >
                    {{ formatChannelType(inbox.channel_type) }}
                  </span>
                </div>
              </div>

              <div v-if="!inboxesList.length" class="col-span-2 text-center py-3 text-xs text-slate-400">
                Nenhuma caixa de entrada carregada.
              </div>
            </div>

            <p class="text-[11px] text-slate-400 dark:text-slate-500">
              💡 Dica: Se nenhuma caixa for marcada, a régua funcionará para qualquer canal do WhatsApp configurado.
            </p>
          </div>

          <!-- Filtro de Etiquetas Obrigatórias e Excluídas (Tags Interativas) -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
            
            <!-- 1. Must Have Labels -->
            <div class="flex flex-col gap-2.5 p-4 rounded-2xl border border-slate-200/70 dark:border-white/10 bg-white/50 dark:bg-[#181920]/60 backdrop-blur-md">
              <div class="flex items-center justify-between">
                <label class="text-xs font-bold text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                  <span>🎯 Tags Obrigatórias (Must Have)</span>
                  <span v-if="form.include_labels.length" class="px-1.5 py-0.2 rounded-full text-[10px] font-bold bg-[#2781F6]/15 text-[#2781F6]">
                    {{ form.include_labels.length }}
                  </span>
                </label>
                <span class="text-[11px] text-slate-500">Vazio = Todos</span>
              </div>

              <!-- Campo de Digitação de Nova Tag -->
              <div class="flex items-center gap-1.5">
                <input
                  v-model="newIncludeTag"
                  @keydown.enter.prevent="addIncludeTag"
                  @keydown.188.prevent="addIncludeTag"
                  type="text"
                  placeholder="Digitar tag + Enter..."
                  class="flex-1 h-8 px-2.5 text-xs bg-white/80 dark:bg-[#1a1b20] border border-slate-200/80 dark:border-slate-700 rounded-lg text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:border-[#2781F6]"
                />
                <button
                  @click="addIncludeTag"
                  type="button"
                  class="h-8 px-3 text-xs font-bold text-white bg-[#2781F6] hover:bg-[#1c6edb] rounded-lg transition-colors cursor-pointer shrink-0"
                >
                  + Add
                </button>
              </div>

              <!-- Tags Ativas Selecionadas -->
              <div class="space-y-1">
                <div class="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Tags ativas nesta régua:
                </div>
                <div class="min-h-9 p-2 bg-white/80 dark:bg-[#141518]/80 rounded-xl border border-slate-200/80 dark:border-slate-700 flex flex-wrap items-center gap-1.5 max-h-28 overflow-y-auto">
                  <span
                    v-for="(tag, idx) in form.include_labels"
                    :key="tag"
                    class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-md bg-[#2781F6]/15 text-[#2781F6] dark:text-[#60a5fa] border border-[#2781F6]/30"
                  >
                    <span>{{ tag }}</span>
                    <button
                      type="button"
                      @click="removeIncludeTag(idx)"
                      class="hover:text-rose-500 text-slate-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded px-0.5 cursor-pointer font-bold transition-colors"
                      title="Remover tag"
                    >
                      ×
                    </button>
                  </span>
                  <span v-if="!form.include_labels.length" class="text-xs text-slate-400 italic">
                    Nenhuma tag obrigatória (todos os leads da etapa entram na régua).
                  </span>
                </div>
              </div>

              <!-- Sugestões Rápidas do Chatwoot -->
              <div v-if="labelsList.length" class="space-y-1 pt-1.5 border-t border-slate-200/60 dark:border-slate-800">
                <span class="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                  Tags existentes no Chatwoot (clique para alternar):
                </span>
                <div class="max-h-24 overflow-y-auto flex flex-wrap gap-1 p-0.5">
                  <span
                    v-for="lbl in labelsList"
                    :key="lbl.id"
                    @click="toggleIncludeLabel(lbl.title)"
                    :class="[
                      form.include_labels.includes(lbl.title)
                        ? 'bg-[#2781F6] text-white font-semibold border-[#2781F6] shadow-2xs'
                        : 'bg-white/80 dark:bg-[#1a1b20] text-slate-600 dark:text-slate-300 border-slate-200/80 dark:border-slate-700 hover:border-[#2781F6]'
                    ]"
                    class="px-2 py-0.5 text-[11px] rounded-md border cursor-pointer select-none transition-all"
                  >
                    {{ lbl.title }}
                  </span>
                </div>
              </div>
            </div>

            <!-- 2. Must Not Have Labels (Blacklist) -->
            <div class="flex flex-col gap-2.5 p-4 rounded-2xl border border-rose-200/70 dark:border-rose-900/40 bg-rose-50/20 dark:bg-rose-950/10 backdrop-blur-md">
              <div class="flex items-center justify-between">
                <label class="text-xs font-bold text-rose-600 dark:text-rose-400 flex items-center gap-1.5">
                  <span>🚫 Tags de Exclusão (Blacklist)</span>
                  <span v-if="form.exclude_labels.length" class="px-1.5 py-0.2 rounded-full text-[10px] font-bold bg-rose-500/15 text-rose-600 dark:text-rose-400">
                    {{ form.exclude_labels.length }}
                  </span>
                </label>
                <span class="text-[11px] text-slate-500">Ignora quem tiver</span>
              </div>

              <!-- Campo de Digitação de Nova Tag de Exclusão -->
              <div class="flex items-center gap-1.5">
                <input
                  v-model="newExcludeTag"
                  @keydown.enter.prevent="addExcludeTag"
                  @keydown.188.prevent="addExcludeTag"
                  type="text"
                  placeholder="Digitar tag de bloqueio + Enter..."
                  class="flex-1 h-8 px-2.5 text-xs bg-white/80 dark:bg-[#1a1b20] border border-rose-200 dark:border-rose-900/60 rounded-lg text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:border-rose-500"
                />
                <button
                  @click="addExcludeTag"
                  type="button"
                  class="h-8 px-3 text-xs font-bold text-white bg-rose-500 hover:bg-rose-600 rounded-lg transition-colors cursor-pointer shrink-0"
                >
                  + Add
                </button>
              </div>

              <!-- Tags Ativas Selecionadas -->
              <div class="space-y-1">
                <div class="text-[10px] font-bold uppercase tracking-wider text-rose-400">
                  Tags que bloqueiam o disparo:
                </div>
                <div class="min-h-9 p-2 bg-white/80 dark:bg-[#141518]/80 rounded-xl border border-rose-200 dark:border-rose-900/50 flex flex-wrap items-center gap-1.5 max-h-28 overflow-y-auto">
                  <span
                    v-for="(tag, idx) in form.exclude_labels"
                    :key="tag"
                    class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-md bg-rose-500/15 text-rose-600 dark:text-rose-400 border border-rose-500/30"
                  >
                    <span>{{ tag }}</span>
                    <button
                      type="button"
                      @click="removeExcludeTag(idx)"
                      class="hover:text-rose-900 dark:hover:text-white text-rose-400 hover:bg-rose-100 dark:hover:bg-rose-900/50 rounded px-0.5 cursor-pointer font-bold transition-colors"
                      title="Remover tag de bloqueio"
                    >
                      ×
                    </button>
                  </span>
                  <span v-if="!form.exclude_labels.length" class="text-xs text-slate-400 italic">
                    Nenhuma tag de bloqueio ativa.
                  </span>
                </div>
              </div>

              <!-- Sugestões Rápidas do Chatwoot -->
              <div v-if="labelsList.length" class="space-y-1 pt-1.5 border-t border-rose-200/60 dark:border-rose-900/40">
                <span class="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                  Tags existentes no Chatwoot (clique para alternar):
                </span>
                <div class="max-h-24 overflow-y-auto flex flex-wrap gap-1 p-0.5">
                  <span
                    v-for="lbl in labelsList"
                    :key="lbl.id"
                    @click="toggleExcludeLabel(lbl.title)"
                    :class="[
                      form.exclude_labels.includes(lbl.title)
                        ? 'bg-rose-500 text-white font-semibold border-rose-500 shadow-2xs'
                        : 'bg-white/80 dark:bg-[#1a1b20] text-slate-600 dark:text-slate-300 border-slate-200/80 dark:border-slate-700 hover:border-rose-400'
                    ]"
                    class="px-2 py-0.5 text-[11px] rounded-md border cursor-pointer select-none transition-all"
                  >
                    {{ lbl.title }}
                  </span>
                </div>
              </div>
            </div>

          </div>
        </div>

        <!-- ================= ABA 2: CADÊNCIA DE PASSOS ================= -->
        <div v-show="activeTab === 'cadence'" class="space-y-4">
          <div class="flex items-center justify-between">
            <div>
              <span class="text-xs font-bold text-slate-800 dark:text-slate-200">
                Sequência e Intervalos de Insistência
              </span>
              <p class="text-xs text-slate-500 dark:text-slate-400">
                Configure o tempo de espera após a inatividade e o direcionamento de cada tentativa.
              </p>
            </div>

            <button
              @click="addCadenceStep"
              type="button"
              class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-[#2781F6] bg-[#2781F6]/10 hover:bg-[#2781F6]/20 border border-[#2781F6]/30 rounded-xl transition-colors cursor-pointer"
            >
              <span class="i-lucide-plus size-3.5" />
              <span>+ Adicionar Tentativa</span>
            </button>
          </div>

          <!-- LISTA DE PASSOS COM CARDS GLASS -->
          <div class="space-y-3">
            <div
              v-for="(step, idx) in form.cadence_steps"
              :key="idx"
              class="p-4 rounded-2xl border border-slate-200/70 dark:border-white/10 bg-white/50 dark:bg-[#181920]/60 backdrop-blur-md shadow-xs space-y-2.5"
            >
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <span class="px-2.5 py-0.5 rounded-lg text-[11px] font-bold bg-[#2781F6]/10 text-[#2781F6] border border-[#2781F6]/30">
                    Tentativa {{ step.step }}
                  </span>
                  <span class="text-xs text-slate-500 font-medium">
                    Aguardar após etapa anterior:
                  </span>
                </div>

                <div class="flex items-center gap-2">
                  <div class="flex items-center gap-1">
                    <input
                      v-model.number="step.delay_hours"
                      type="number"
                      min="1"
                      class="w-16 h-7 px-2 text-xs font-bold text-center bg-white/80 dark:bg-[#141518] border border-slate-200/80 dark:border-slate-700 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:border-[#2781F6]"
                    />
                    <span class="text-xs font-bold text-slate-600 dark:text-slate-400">horas</span>
                  </div>

                  <button
                    v-if="form.cadence_steps.length > 1"
                    @click="removeCadenceStep(idx)"
                    type="button"
                    class="p-1.5 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30 transition-colors"
                    title="Remover passo"
                  >
                    <span class="i-lucide-trash-2 size-3.5" />
                  </button>
                </div>
              </div>

              <!-- Prompt Específico do Passo -->
              <div>
                <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                  Direcionamento da Mensagem para esta Tentativa:
                </label>
                <input
                  v-model="step.step_prompt"
                  placeholder="Ex: Perguntar se restou dúvida sobre os prazos de forma cordial."
                  class="w-full h-9 px-3 text-xs bg-white/80 dark:bg-[#141518] border border-slate-200/80 dark:border-slate-700 rounded-xl text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:border-[#2781F6]"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- ================= ABA 3: INTELIGÊNCIA ARTIFICIAL ================= -->
        <div v-show="activeTab === 'ai'" class="space-y-4">
          <!-- Modo de Geração -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label
              :class="form.generation_type === 'ai' ? 'border-[#2781F6] bg-[#2781F6]/10 ring-1 ring-[#2781F6]' : 'border-slate-200/70 dark:border-white/10 bg-white/50 dark:bg-[#181920]/60'"
              class="flex items-start gap-3 p-4 rounded-2xl border cursor-pointer select-none backdrop-blur-md transition-all"
            >
              <input
                type="radio"
                value="ai"
                v-model="form.generation_type"
                class="mt-0.5 size-4 text-[#2781F6] focus:ring-0 cursor-pointer"
              />
              <div>
                <span class="block text-xs font-black text-slate-900 dark:text-white flex items-center gap-1.5">
                  <span>🤖 Mensagem Adaptativa com IA</span>
                  <span class="px-1.5 py-0.2 rounded text-[9px] font-bold bg-[#2781F6]/15 text-[#2781F6]">Recomendado</span>
                </span>
                <span class="block text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                  A IA lê o histórico da conversa e gera uma mensagem única personalizada para o lead.
                </span>
              </div>
            </label>

            <label
              :class="form.generation_type === 'fixed' ? 'border-[#2781F6] bg-[#2781F6]/10 ring-1 ring-[#2781F6]' : 'border-slate-200/70 dark:border-white/10 bg-white/50 dark:bg-[#181920]/60'"
              class="flex items-start gap-3 p-4 rounded-2xl border cursor-pointer select-none backdrop-blur-md transition-all"
            >
              <input
                type="radio"
                value="fixed"
                v-model="form.generation_type"
                class="mt-0.5 size-4 text-[#2781F6] focus:ring-0 cursor-pointer"
              />
              <div>
                <span class="block text-xs font-black text-slate-900 dark:text-white">
                  ✉️ Template Fixo com Variáveis
                </span>
                <span class="block text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                  Utiliza texto padronizado com substituição de <code>&#123;&#123; contact.name &#125;&#125;</code>.
                </span>
              </div>
            </label>
          </div>

          <!-- Configurações de IA -->
          <div v-if="form.generation_type === 'ai'" class="space-y-3.5">
            <!-- Modelo de IA -->
            <div class="flex flex-col gap-1.5">
              <div class="flex items-center justify-between">
                <label class="text-xs font-bold text-slate-800 dark:text-slate-200">
                  Modelo de IA
                </label>
                <span class="text-xs text-[#2781F6] font-bold">Motor OpenAI Ativo</span>
              </div>
              <select
                v-model="form.ai_model"
                class="w-full h-10 px-3.5 text-xs font-medium bg-white/70 dark:bg-[#1a1b20]/70 border border-slate-200/80 dark:border-[#2c2d33] rounded-xl text-slate-900 dark:text-slate-100 focus:outline-none focus:border-[#2781F6] cursor-pointer"
              >
                <option value="gpt-5.6-luna">⚡ GPT-5.6 Luna / 4o (Recomendado para Advocacia & Vendas)</option>
                <option value="gpt-4o">🧠 OpenAI GPT-4o (Raciocínio Jurídico Avançado)</option>
                <option value="gpt-4o-mini">🚀 OpenAI GPT-4o-mini (Ultra Rápido)</option>
              </select>
            </div>

            <!-- Prompt Base do Sistema -->
            <div class="flex flex-col gap-1.5">
              <div class="flex items-center justify-between">
                <label class="text-xs font-bold text-slate-800 dark:text-slate-200">
                  Prompt Base da Persona / Escritório
                </label>
                <span class="text-xs text-slate-500">Diretrizes de tom e personalidade</span>
              </div>
              <textarea
                v-model="form.ai_prompt"
                rows="4"
                placeholder="Ex: Você é um assistente humanizado do escritório Zéti Juris. Seja empático, acolhedor e direto ao ponto no WhatsApp..."
                class="w-full p-3.5 text-xs bg-white/70 dark:bg-[#1a1b20]/70 border border-slate-200/80 dark:border-[#2c2d33] rounded-xl text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:border-[#2781F6] focus:bg-white dark:focus:bg-[#202128] transition-all resize-none"
              />
            </div>

            <!-- Card de Simulação ao Vivo de IA (Design Glass) -->
            <div class="p-4 rounded-2xl border border-[#2781F6]/30 bg-[#2781F6]/5 backdrop-blur-md space-y-3">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <span class="text-[#2781F6]">
                    <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </span>
                  <span class="text-xs font-black text-slate-900 dark:text-white">
                    Simulador em Tempo Real com IA
                  </span>
                </div>

                <button
                  @click="simulateAi"
                  :disabled="isSimulating || !form.funnel_attribute_key"
                  type="button"
                  class="flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold text-white bg-[#2781F6] hover:bg-[#1c6edb] rounded-xl shadow-xs transition-colors cursor-pointer disabled:opacity-50"
                >
                  <span v-if="!isSimulating" class="i-lucide-play size-3" />
                  <span v-else class="i-lucide-loader-2 size-3 animate-spin" />
                  <span>{{ isSimulating ? 'Gerando...' : 'Testar Resposta IA' }}</span>
                </button>
              </div>

              <div v-if="simulatedResult" class="p-3 bg-white/90 dark:bg-[#141518]/90 rounded-xl border border-slate-200/80 dark:border-slate-700 text-xs text-slate-800 dark:text-slate-200 space-y-1">
                <div class="text-[10px] font-bold text-[#2781F6] uppercase tracking-wider">
                  Exemplo de Mensagem Gerada:
                </div>
                <p class="leading-relaxed italic">
                  "{{ simulatedResult.message || simulatedResult }}"
                </p>
              </div>
            </div>
          </div>

          <!-- Template Fixo (Se Selecionado) -->
          <div v-else class="flex flex-col gap-1.5">
            <label class="text-xs font-bold text-slate-800 dark:text-slate-200">
              Texto do Template Fixo
            </label>
            <textarea
              v-model="form.fixed_template"
              rows="4"
              placeholder="Olá {{ contact.first_name }}, tudo bem? Passando para saber se restou alguma dúvida sobre o orçamento enviado..."
              class="w-full p-3.5 text-xs bg-white/70 dark:bg-[#1a1b20]/70 border border-slate-200/80 dark:border-[#2c2d33] rounded-xl text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:border-[#2781F6] resize-none"
            />
          </div>
        </div>

        <!-- ================= ABA 4: ENVIO & HORÁRIOS ================= -->
        <div v-show="activeTab === 'settings'" class="space-y-4">
          
          <!-- MODO DE DISPARO (AUTOMÁTICO vs CO-PILOTO/SUGESTÃO) -->
          <div class="space-y-2">
            <label class="block text-xs font-bold text-slate-800 dark:text-slate-200">
              Modo de Disparo do Follow-up *
            </label>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <!-- Opção Automático -->
              <label
                :class="form.send_mode === 'automatic' ? 'border-[#2781F6] bg-[#2781F6]/10 ring-1 ring-[#2781F6]' : 'border-slate-200/70 dark:border-white/10 bg-white/50 dark:bg-[#181920]/60'"
                class="flex items-start gap-3 p-4 rounded-2xl border cursor-pointer select-none backdrop-blur-md transition-all"
              >
                <input
                  type="radio"
                  value="automatic"
                  v-model="form.send_mode"
                  class="mt-0.5 size-4 text-[#2781F6] focus:ring-0 cursor-pointer"
                />
                <div>
                  <span class="block text-xs font-black text-slate-900 dark:text-white flex items-center gap-1.5">
                    <span>🚀 Disparo Automático</span>
                    <span class="px-1.5 py-0.2 rounded text-[9px] font-bold bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">WhatsApp Direto</span>
                  </span>
                  <span class="block text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">
                    A mensagem é disparada diretamente para o WhatsApp do cliente sem necessidade de intervenção humana.
                  </span>
                </div>
              </label>

              <!-- Opção Co-piloto / Sugestão -->
              <label
                :class="form.send_mode === 'suggestion' ? 'border-[#2781F6] bg-[#2781F6]/10 ring-1 ring-[#2781F6]' : 'border-slate-200/70 dark:border-white/10 bg-white/50 dark:bg-[#181920]/60'"
                class="flex items-start gap-3 p-4 rounded-2xl border cursor-pointer select-none backdrop-blur-md transition-all"
              >
                <input
                  type="radio"
                  value="suggestion"
                  v-model="form.send_mode"
                  class="mt-0.5 size-4 text-[#2781F6] focus:ring-0 cursor-pointer"
                />
                <div>
                  <span class="block text-xs font-black text-slate-900 dark:text-white flex items-center gap-1.5">
                    <span>🤖 Co-piloto / Sugestão</span>
                    <span class="px-1.5 py-0.2 rounded text-[9px] font-bold bg-amber-500/15 text-amber-600 dark:text-amber-400">Nota Privada</span>
                  </span>
                  <span class="block text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">
                    Cria uma nota privada interna com a mensagem sugerida para o atendente revisar ou enviar com 1 clique.
                  </span>
                </div>
              </label>
            </div>
          </div>

          <!-- Switch: Régua Ativa -->
          <div class="flex items-center justify-between p-4 rounded-2xl border border-slate-200/70 dark:border-white/10 bg-white/50 dark:bg-[#181920]/60 backdrop-blur-md">
            <div>
              <span class="block text-xs font-bold text-slate-800 dark:text-slate-200">
                Régua Ativa no Motor de Follow-up
              </span>
              <span class="block text-[11px] text-slate-500 dark:text-slate-400">
                Quando ativa, as conversas que entrarem nesta etapa serão avaliadas automaticamente
              </span>
            </div>

            <!-- TOGGLE SWITCH NATIVO -->
            <label class="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                v-model="form.is_active"
                class="sr-only peer"
              />
              <div class="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:bg-[#2781F6] transition-colors relative after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
            </label>
          </div>

          <!-- Switch: Apenas Horário Comercial -->
          <div class="flex items-center justify-between p-4 rounded-2xl border border-slate-200/70 dark:border-white/10 bg-white/50 dark:bg-[#181920]/60 backdrop-blur-md">
            <div>
              <span class="block text-xs font-bold text-slate-800 dark:text-slate-200">
                Disparar Apenas em Horário Comercial
              </span>
              <span class="block text-[11px] text-slate-500 dark:text-slate-400">
                Evita envios durante a noite ou fins de semana
              </span>
            </div>

            <!-- TOGGLE SWITCH NATIVO -->
            <label class="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                v-model="form.business_hours_only"
                class="sr-only peer"
              />
              <div class="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:bg-[#2781F6] transition-colors relative after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
            </label>
          </div>

          <!-- Faixa de Horário -->
          <div v-if="form.business_hours_only" class="grid grid-cols-2 gap-3.5 p-4 rounded-2xl bg-white/60 dark:bg-[#141518]/60 border border-slate-200/80 dark:border-slate-700">
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Horário de Início
              </label>
              <input
                v-model="form.business_start_time"
                type="time"
                class="w-full h-9 px-3 text-xs bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:border-[#2781F6]"
              />
            </div>

            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Horário de Término
              </label>
              <input
                v-model="form.business_end_time"
                type="time"
                class="w-full h-9 px-3 text-xs bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:border-[#2781F6]"
              />
            </div>
          </div>
        </div>

      </div>

      <!-- FOOTER DO MODAL (DESIGN GLASS) -->
      <div class="flex items-center justify-between px-6 py-4 border-t border-slate-200/60 dark:border-white/10 bg-white/40 dark:bg-[#141518]/40 backdrop-blur-md">
        <button
          @click="$emit('close')"
          type="button"
          class="px-4 py-2 text-xs font-bold rounded-xl bg-slate-100/80 dark:bg-[#1a1b20] text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-[#26282d] transition-all cursor-pointer"
        >
          Cancelar
        </button>

        <button
          @click="saveRule"
          :disabled="isSaving || !isValid"
          type="button"
          class="px-5 py-2.5 text-xs font-black rounded-xl bg-[#2781F6] hover:bg-[#1c6edb] text-white shadow-md shadow-blue-500/20 active:scale-98 transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
        >
          <span v-if="!isSaving" class="i-lucide-check size-4" />
          <span v-else class="i-lucide-loader-2 size-4 animate-spin" />
          <span>{{ isSaving ? 'Salvando...' : (isEditing ? 'Salvar Alterações' : 'Criar Régua de Follow-up') }}</span>
        </button>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import FollowUpsAPI from 'dashboard/api/followUps';

const props = defineProps({
  rule: { type: Object, default: null },
  availableModels: { type: Array, default: () => [] },
  inboxes: { type: Array, default: () => [] },
  labels: { type: Array, default: () => [] }
});

const emit = defineEmits(['close', 'saved']);

const store = useStore();
const activeTab = ref('general');
const isSaving = ref(false);
const isSimulating = ref(false);
const simulatedResult = ref(null);

const newIncludeTag = ref('');
const newExcludeTag = ref('');

const isEditing = computed(() => !!props.rule?.id);

const tabs = [
  { id: 'general', name: '1. Funil & Gatilhos', icon: 'i-lucide-layers' },
  { id: 'cadence', name: '2. Cadência', icon: 'i-lucide-git-commit' },
  { id: 'ai', name: '3. Inteligência Artificial', icon: 'i-lucide-sparkles' },
  { id: 'settings', name: '4. Envio & Horários', icon: 'i-lucide-clock' },
];

const form = ref({
  name: '',
  funnel_attribute_key: '',
  stage_name: '',
  inbox_ids: [],
  include_labels: [],
  exclude_labels: [],
  is_active: true,
  send_mode: 'automatic',
  generation_type: 'ai',
  ai_model: 'gpt-5.6-luna',
  ai_prompt: 'Você é um assistente humanizado do escritório de advocacia Zéti Juris. Seja empático, acolhedor e direto ao ponto no WhatsApp. Use o primeiro nome do cliente e cite o assunto tratado.',
  fixed_template: '',
  cadence_steps: [
    { step: 1, delay_hours: 4, step_prompt: 'Perguntar se restou alguma dúvida sobre a proposta enviada de forma prestativa.' },
    { step: 2, delay_hours: 24, step_prompt: 'Oferecer facilidade no parcelamento e esclarecer detalhes para agilizar o processo.' },
    { step: 3, delay_hours: 72, step_prompt: 'Última tentativa cordial avisando que estamos à disposição caso queira retomar no futuro.' }
  ],
  business_hours_only: false,
  business_start_time: '08:30',
  business_end_time: '19:00',
  workdays_only: true
});

const customAttributesList = computed(() => store.getters['attributes/getAttributesByModel']('conversation_attribute') || []);
const funnelsList = computed(() => customAttributesList.value.filter(a => a.attribute_display_type === 'list'));

const inboxesList = computed(() => {
  if (props.inboxes && props.inboxes.length) return props.inboxes;
  return store.getters['inboxes/getInboxes'] || [];
});

const labelsList = computed(() => {
  if (props.labels && props.labels.length) return props.labels;
  return store.getters['labels/getLabels'] || [];
});

const currentFunnelStages = computed(() => {
  if (!form.value.funnel_attribute_key) return [];
  const funnel = funnelsList.value.find(f => f.attribute_key === form.value.funnel_attribute_key);
  return funnel?.attribute_values || [];
});

const isValid = computed(() => {
  return form.value.name.trim() && form.value.funnel_attribute_key && form.value.stage_name;
});

const onFunnelChange = () => {
  if (currentFunnelStages.value.length) {
    form.value.stage_name = currentFunnelStages.value[0];
  } else {
    form.value.stage_name = '';
  }
};

const toggleInbox = (id) => {
  const index = form.value.inbox_ids.indexOf(id);
  if (index > -1) {
    form.value.inbox_ids.splice(index, 1);
  } else {
    form.value.inbox_ids.push(id);
  }
};

const selectAllInboxes = () => {
  form.value.inbox_ids = inboxesList.value.map(i => i.id);
};

const clearAllInboxes = () => {
  form.value.inbox_ids = [];
};

const formatChannelType = (type) => {
  if (!type) return 'Canal';
  if (type.includes('Whatsapp') || type.includes('Api')) return 'WhatsApp';
  if (type.includes('Email')) return 'E-Mail';
  if (type.includes('WebWidget')) return 'Chat Web';
  return type.replace('Channel::', '');
};

const getChannelBadgeClass = (type) => {
  if (!type) return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400';
  if (type.includes('Whatsapp') || type.includes('Api')) {
    return 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30';
  }
  if (type.includes('Email')) {
    return 'bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30';
  }
  return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400';
};

const addIncludeTag = () => {
  const clean = newIncludeTag.value.trim().replace(/,/g, '');
  if (clean && !form.value.include_labels.includes(clean)) {
    form.value.include_labels.push(clean);
  }
  newIncludeTag.value = '';
};

const removeIncludeTag = (idx) => {
  form.value.include_labels.splice(idx, 1);
};

const toggleIncludeLabel = (lblTitle) => {
  const idx = form.value.include_labels.indexOf(lblTitle);
  if (idx > -1) {
    form.value.include_labels.splice(idx, 1);
  } else {
    form.value.include_labels.push(lblTitle);
  }
};

const addExcludeTag = () => {
  const clean = newExcludeTag.value.trim().replace(/,/g, '');
  if (clean && !form.value.exclude_labels.includes(clean)) {
    form.value.exclude_labels.push(clean);
  }
  newExcludeTag.value = '';
};

const removeExcludeTag = (idx) => {
  form.value.exclude_labels.splice(idx, 1);
};

const toggleExcludeLabel = (lblTitle) => {
  const idx = form.value.exclude_labels.indexOf(lblTitle);
  if (idx > -1) {
    form.value.exclude_labels.splice(idx, 1);
  } else {
    form.value.exclude_labels.push(lblTitle);
  }
};

const addCadenceStep = () => {
  const nextStep = form.value.cadence_steps.length + 1;
  const lastDelay = form.value.cadence_steps[form.value.cadence_steps.length - 1]?.delay_hours || 24;
  form.value.cadence_steps.push({
    step: nextStep,
    delay_hours: lastDelay * 2,
    step_prompt: `Instrução para a tentativa ${nextStep}`
  });
};

const removeCadenceStep = (idx) => {
  form.value.cadence_steps.splice(idx, 1);
  form.value.cadence_steps.forEach((s, i) => { s.step = i + 1; });
};

const simulateAi = async () => {
  isSimulating.value = true;
  simulatedResult.value = null;
  try {
    const payload = {
      funnel_attribute_key: form.value.funnel_attribute_key,
      stage_name: form.value.stage_name,
      ai_model: form.value.ai_model,
      ai_prompt: form.value.ai_prompt,
      generation_type: form.value.generation_type,
      fixed_template: form.value.fixed_template,
      step: 1,
      step_prompt: form.value.cadence_steps[0]?.step_prompt || ''
    };
    const response = await FollowUpsAPI.simulateAi(payload);
    if (response?.data) {
      simulatedResult.value = response.data;
    }
  } catch (err) {
    console.error('Simulation failed:', err);
  } finally {
    isSimulating.value = false;
  }
};

const saveRule = async () => {
  isSaving.value = true;
  try {
    const payload = {
      ...form.value,
      inbox_ids: form.value.inbox_ids || [],
      include_labels: form.value.include_labels || [],
      exclude_labels: form.value.exclude_labels || []
    };
    if (isEditing.value) {
      await FollowUpsAPI.updateRule(props.rule.id, payload);
    } else {
      await FollowUpsAPI.createRule(payload);
    }
    emit('saved');
  } catch (err) {
    console.error('Save rule failed:', err);
    alert('Erro ao salvar régua de follow-up: ' + (err.response?.data?.errors?.join(', ') || err.message));
  } finally {
    isSaving.value = false;
  }
};

onMounted(() => {
  store.dispatch('attributes/get', 0);
  store.dispatch('inboxes/get');
  store.dispatch('labels/get');

  if (props.rule) {
    form.value = {
      name: props.rule.name || '',
      funnel_attribute_key: props.rule.funnel_attribute_key || '',
      stage_name: props.rule.stage_name || '',
      inbox_ids: Array.isArray(props.rule.inbox_ids) ? [...props.rule.inbox_ids] : [],
      include_labels: Array.isArray(props.rule.include_labels) ? [...props.rule.include_labels] : [],
      exclude_labels: Array.isArray(props.rule.exclude_labels) ? [...props.rule.exclude_labels] : [],
      is_active: props.rule.is_active !== false,
      send_mode: props.rule.send_mode || 'automatic',
      generation_type: props.rule.generation_type || 'ai',
      ai_model: props.rule.ai_model || 'gpt-5.6-luna',
      ai_prompt: props.rule.ai_prompt || '',
      fixed_template: props.rule.fixed_template || '',
      cadence_steps: props.rule.cadence_steps?.length ? JSON.parse(JSON.stringify(props.rule.cadence_steps)) : [
        { step: 1, delay_hours: 4, step_prompt: 'Perguntar se restou alguma dúvida sobre a proposta.' },
        { step: 2, delay_hours: 24, step_prompt: 'Oferecer condição facilitada.' }
      ],
      business_hours_only: props.rule.business_hours_only === true,
      business_start_time: props.rule.business_start_time || '08:30',
      business_end_time: props.rule.business_end_time || '19:00',
      workdays_only: props.rule.workdays_only !== false
    };
  } else if (funnelsList.value.length) {
    form.value.funnel_attribute_key = funnelsList.value[0].attribute_key;
    onFunnelChange();
  }
});
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

.followup-modal-root {
  font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

@keyframes modalFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes modalZoomIn {
  from {
    opacity: 0;
    transform: scale(0.96) translateY(6px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.animate-fade-in {
  animation: modalFadeIn 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.animate-zoom-in {
  animation: modalZoomIn 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
</style>
